import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../lib/sanity';
import { newsArticles } from '../data/mock';

const RECENT_POSTS_QUERY = `*[_type == "post"] | order(publishedAt desc) [0...3] {
  _id, title, slug, category, excerpt, publishedAt
}`;

const categoryColor = (cat) => {
  const green = ['Leadership', 'Performance'];
  if (green.includes(cat)) return { bg: 'rgba(27,61,42,0.2)', border: 'rgba(27,61,42,0.5)', text: '#4ade80' };
  return { bg: 'rgba(200,16,46,0.12)', border: 'rgba(200,16,46,0.4)', text: '#ff6b7a' };
};

const NewsSection = () => {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    if (!client) return;
    client
      .fetch(RECENT_POSTS_QUERY)
      .then((data) => {
        if (data?.length) {
          setArticles(
            data.map((p) => ({
              id: p._id,
              slug: p.slug?.current,
              category: p.category || 'Insight',
              title: p.title,
              date: p.publishedAt
                ? new Date(p.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '',
              excerpt: p.excerpt || '',
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  // Fall back to mock data only if Sanity returned nothing
  const displayArticles = articles || newsArticles.map((a) => ({ ...a, slug: null }));

  return (
    <section id="insights" style={{
      backgroundColor: '#0a0a0a',
      padding: '120px 0',
      borderTop: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 40px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '64px' }}>
          <div>
            <span style={{
              color: 'rgba(255,255,255,0.4)',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '12px',
              fontWeight: '500',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '16px',
            }}>News & Insights</span>
            <h2 style={{
              color: '#ffffff',
              fontFamily: "'Climate Crisis', sans-serif",
              fontSize: 'clamp(28px, 4vw, 48px)',
              fontWeight: '400',
              letterSpacing: '0em',
              lineHeight: '1.1',
              margin: 0,
              maxWidth: '500px',
            }}>
              Thoughts on Sport, Governance & Leadership
            </h2>
          </div>
          <Link
            to="/blog"
            style={{
              background: 'transparent',
              color: 'rgba(255,255,255,0.6)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '100px',
              padding: '10px 22px',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'border-color 0.2s ease, color 0.2s ease',
              textDecoration: 'none',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >
            View All
          </Link>
        </div>

        {/* Articles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {displayArticles.map((article) => {
            const colors = categoryColor(article.category);
            const row = (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr auto',
                  gap: '32px',
                  alignItems: 'start',
                  padding: '36px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                className="article-row"
              >
                {/* Category + date */}
                <div>
                  <div style={{
                    display: 'inline-block',
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '20px',
                    padding: '4px 10px',
                    marginBottom: '12px',
                  }}>
                    <span style={{
                      color: colors.text,
                      fontFamily: "'Figtree', sans-serif",
                      fontSize: '10px',
                      fontWeight: '700',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}>
                      {article.category}
                    </span>
                  </div>
                  <div style={{
                    color: 'rgba(255,255,255,0.3)',
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: '12px',
                    fontWeight: '400',
                  }}>
                    {article.date}
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h3 style={{
                    color: '#ffffff',
                    fontFamily: "'Climate Crisis', sans-serif",
                    fontSize: 'clamp(16px, 2vw, 22px)',
                    fontWeight: '400',
                    letterSpacing: '0em',
                    lineHeight: '1.3',
                    marginBottom: '12px',
                  }}>
                    {article.title}
                  </h3>
                  <p style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '1.7',
                    margin: 0,
                  }}>
                    {article.excerpt}
                  </p>
                </div>

                {/* Arrow */}
                <div style={{ paddingTop: '4px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 11L11 3M11 3H5M11 3V9" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            );

            return article.slug ? (
              <Link key={article.id} to={`/blog/${article.slug}`} style={{ textDecoration: 'none' }}>
                {row}
              </Link>
            ) : (
              <div key={article.id}>{row}</div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .article-row {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default NewsSection;
